import { useMemo, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, HelpCircle } from "lucide-react";
import { SectionHeader } from "@/components/ui/section-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Reveal } from "@/components/motion/Reveal";

export default function Faq() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<string>("all");

  const items = useMemo(() => ([
    { cat: "bootcamps", q: t("marketing.faq.q1"), a: t("marketing.faq.a1") },
    { cat: "bootcamps", q: t("marketing.faq.q2"), a: t("marketing.faq.a2") },
    { cat: "downloads", q: t("marketing.faq.q3"), a: t("marketing.faq.a3") },
    { cat: "coaching", q: t("marketing.faq.q4"), a: t("marketing.faq.a4") },
    { cat: "coaching", q: t("marketing.faq.q5"), a: t("marketing.faq.a5") },
    { cat: "payments", q: t("marketing.faq.q6"), a: t("marketing.faq.a6") },
    { cat: "payments", q: t("marketing.faq.q7"), a: t("marketing.faq.a7") },
    { cat: "certs",     q: t("marketing.faq.q8"), a: t("marketing.faq.a8") },
  ]), [t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== "all" && it.cat !== cat) return false;
      if (!q) return true;
      return it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q);
    });
  }, [items, query, cat]);

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  }), [items]);

  return (
    <PublicShell>
      <Seo title={t("marketing.faq.seoTitle")} description={t("marketing.faq.seoDescription")} jsonLd={faqJsonLd} />

      <section className="container py-12 md:py-16">
        <Reveal>
        <SectionHeader
          align="center"
          className="mb-10"
          eyebrow={t("marketing.faq.eyebrow", { defaultValue: "Help center" })}
          title={t("marketing.faq.title", { defaultValue: "Frequently asked questions" })}
          lede={t("marketing.faq.subtitle", { defaultValue: "Answers about bootcamps, downloads, coaching, payments, and certificates." })}
        />
        </Reveal>
        <div className="mx-auto max-w-2xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("marketing.faq.searchPlaceholder")}
              className="pl-9"
            />
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-3xl">
          <Tabs value={cat} onValueChange={setCat}>
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6">
              <TabsTrigger value="all">{t("marketing.catalog.all")}</TabsTrigger>
              <TabsTrigger value="bootcamps">{t("marketing.faq.catBootcamps")}</TabsTrigger>
              <TabsTrigger value="downloads">{t("marketing.faq.catDownloads")}</TabsTrigger>
              <TabsTrigger value="coaching">{t("marketing.faq.catCoaching")}</TabsTrigger>
              <TabsTrigger value="payments">{t("marketing.faq.catPayments")}</TabsTrigger>
              <TabsTrigger value="certs">{t("marketing.faq.catCerts")}</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="mt-6">
            {filtered.length === 0 ? (
              <EmptyState
                illustration={<HelpCircle className="h-10 w-10" />}
                title={t("marketing.faq.empty")}
                action={
                  <Button variant="outline" size="sm" onClick={() => { setQuery(""); setCat("all"); }}>
                    {t("marketing.faq.reset", { defaultValue: "Reset filters" })}
                  </Button>
                }
              />
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {filtered.map((it, i) => (
                  <AccordionItem key={i} value={`item-${i}`}>
                    <AccordionTrigger className="text-left">{it.q}</AccordionTrigger>
                    <AccordionContent>{it.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <Reveal className="container py-12 text-center">
          <h2 className="t-display-sm">{t("marketing.faq.stillTitle")}</h2>
          <p className="mt-2 text-muted-foreground">{t("marketing.faq.stillSubtitle")}</p>
          <Button asChild variant="brand" className="mt-6">
            <Link to="/contact">{t("marketing.faq.stillCta")}</Link>
          </Button>
        </Reveal>
      </section>
    </PublicShell>
  );
}
